import React, { useState, useEffect } from "react";
import { Grid2 } from "@mui/material";
import styles from "./LicenseUsageStyle.module.scss";
import { Bar } from "react-chartjs-2";
import { Gauge, gaugeClasses } from "@mui/x-charts/Gauge";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import TimeRange from "../../../../GlobalComponents/TimeRange/TimeRange";
import { ReactComponent as Line } from "../../AdminAssets/svg/Line1.svg";
import { formatFileSizeCaps } from "../../../../Utils/roconConstants";
import PlanLineGraph from "../../Pages/Billing/Components/PlanLineGraph/PlanLineGraph";
import { useLocation, useParams } from "react-router-dom";
import NoGraphData from "../../Pages/Billing/Components/NoGraphData/NoGraphData";
import TooltipType from "../../../../GlobalComponents/TooltipComponent/TooltipComponent";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

function LicenseUsage({
  bandwidthGraphResponse,
  storageGraphResponse,
  visitsGraphResponse,
  maxBandwidth,
  setSelectedUsageRange,
  visitsLabel,
  visitsTooltip,
}) {
  const params = useParams();
  const location = useLocation();
  const [gaugeValue, setGaugeValue] = useState(0);
  const [animatedGaugeValue, setAnimatedGaugeValue] = useState(0);
  const isMobile = window.innerWidth <= 768;

  // Set gauge value to the latest bandwidth data (last item in bandwidthGraphResponse)
  useEffect(() => {
    const totalBandwidth =
      bandwidthGraphResponse?.reduce(
        (sum, item) => sum + Number(item?.value || 0),
        0
      ) || 0;
    setTimeout(() => setGaugeValue(totalBandwidth), 250);
  }, [bandwidthGraphResponse]);

  useEffect(() => {
    let start = 0;
    const end = gaugeValue;
    const duration = 1000;
    const increment = (end - start) / (duration / 16);

    const animate = () => {
      start += increment;
      if (start >= end) start = end;
      setAnimatedGaugeValue(Math.round(start));
      if (start < end) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);

    return () => setAnimatedGaugeValue(end);
  }, [gaugeValue]);

  // Storage Usage Data
  const storageData = storageGraphResponse
    ? {
        labels: storageGraphResponse.map((item) => item.label),
        datasets: [
          {
            data: storageGraphResponse.map((item) => {
              const formattedValue = parseFloat(item.value) / (1024 * 1024);
              return formattedValue;
            }),
            backgroundColor: generateColors(storageGraphResponse.length),
            borderColor: generateColors(storageGraphResponse.length, true),
            borderWidth: 1,
            hoverBackgroundColor: generateHoverColors(
              storageGraphResponse.length
            ),
            hoverBorderColor: generateHoverColors(
              storageGraphResponse.length,
              true
            ),
            borderRadius: 50,
            barThickness: 15,
            maxBarThickness: 30,
          },
        ],
      }
    : {
        labels: ["No Data"],
        datasets: [
          {
            data: [0],
            backgroundColor: "rgba(128, 128, 128, 0.2)",
          },
        ],
      };

  const options = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (tooltipItem) => {
            const valueInMB = tooltipItem.raw; // Raw numeric value
            return `${valueInMB.toFixed(2)} Mb`; // Show value with "MB"
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true },
        grid: { display: false },
        ticks: {
          autoSkip: false,
        },
      },
      y: {
        title: { display: true, text: "Usage in MB" },
        beginAtZero: true,
        grid: { display: false },
      },
    },
  };

  // Visits Usage Data
  const visitsData =
    visitsGraphResponse?.map((item) => ({
      label: item.label,
      value: item.value,
    })) || [];

  const bandwidthData =
    bandwidthGraphResponse?.map((item) => ({
      label: item.label,
      value: item.value,
    })) || [];

  function getGaugeColor(value, max) {
    const percentage = (value / max) * 100;
    if (percentage >= 90) return "red";
    if (percentage >= 70) return "#e39014";
  }

  const searchPaths = `/account/${params.orgId}/${params.siteId}/site-details`;
  const lineBandwith = searchPaths === location.pathname;

  return (
    <Grid2 className={styles.LicenseUsageContainer}>
      <Grid2 container className={styles.labelContainer}>
        <Grid2 className={styles.label}>Plan Usage</Grid2>
        <TimeRange onRangeSelect={setSelectedUsageRange} />
      </Grid2>
      {storageGraphResponse || bandwidthGraphResponse || visitsGraphResponse ? (
        <Grid2 item className={styles.licenseUsageContainer}>
          {/* Storage Usage */}
          <Grid2 className={styles.licenseUsageItem}>
            <Grid2 className={styles.UsageText}>Storage Usage</Grid2>
            <Grid2 className={styles.chartFlexBox}>
              <Grid2 className={styles.barChart}>
                <Bar data={storageData} options={options} />
              </Grid2>
              <Grid2 className={styles.line}>{!isMobile && <Line />}</Grid2>
            </Grid2>
            {isMobile && <div className={styles.dottedLine} />}
          </Grid2>
          {/* Bandwidth Usage */}
          <Grid2 className={styles.licenseUsageItem}>
            <Grid2 className={styles.UsageText}>Bandwidth Usage</Grid2>
            <Grid2 className={styles.chartFlexBox}>
              <Grid2 className={styles.GaugeChart}>
                {lineBandwith ? (
                  <PlanLineGraph data={bandwidthData} storage={true} />
                ) : (
                  <Gauge
                    value={formatFileSizeCaps(animatedGaugeValue)}
                    max={maxBandwidth}
                    startAngle={-90}
                    endAngle={90}
                    sx={{
                      [`& .${gaugeClasses.valueText}`]: {
                        fontSize: 18,
                        transform: "translate(0px, -40px)",
                        transition: "all 1.5s ease",
                      },
                      [`& .${gaugeClasses.arc}`]: {
                        transition: "stroke-dashoffset 1.5s ease",
                      },
                      // Change color of arc according to the value
                      [`& .${gaugeClasses.valueArc}`]: {
                        fill: getGaugeColor(animatedGaugeValue, maxBandwidth),
                      },
                    }}
                    text={() =>
                      `${formatFileSizeCaps(animatedGaugeValue)} / ${
                        maxBandwidth ? maxBandwidth : 0
                      }GB `
                    }
                  />
                )}
              </Grid2>
              <Grid2 className={styles.line}>{!isMobile && <Line />}</Grid2>
            </Grid2>
            {isMobile && <div className={styles.dottedLine} />}
          </Grid2>
          {/* Visits Usage */}
          <Grid2 className={styles.licenseUsageItem}>
            {visitsTooltip ? (
              <TooltipType title={visitsTooltip}>
                <Grid2 className={styles.UsageText}>
                  {visitsLabel || "Visits Usage"}
                </Grid2>
              </TooltipType>
            ) : (
              <Grid2 className={styles.UsageText}>
                {visitsLabel || "Visits Usage"}
              </Grid2>
            )}
            <Grid2 className={styles.chartFlexBox}>
              <Grid2 className={styles.LineChart}>
                <PlanLineGraph data={visitsData} storage={false} />
              </Grid2>
            </Grid2>
          </Grid2>
        </Grid2>
      ) : (
        <NoGraphData />
      )}
    </Grid2>
  );
}

// Helper functions
function generateColors(length, border = false) {
  const color = border ? "#135FC2" : "rgba(128, 128, 128, 0.2)";
  return Array(length)
    .fill(color)
    .map((color, index) => (index === length - 1 ? "#135FC2" : color));
}

function generateHoverColors(length, border = false) {
  return Array(length).fill(border ? "#135FC2" : "#135FC2");
}

export default LicenseUsage;
